import { useTranslate } from '@tolgee/react';
import { useAuthz } from '~/context/AuthzContext';
import { WrapperWidget } from '@agentselly/ui';

// import { useAppParams } from '~/helper/useAppParams';

type StageStatus = 'complete' | 'active' | 'upcoming' | 'blocked';

type Stage = {
  key: string;
  labelKey: string;
  status: StageStatus;
  progress?: number;
  kpi?: number;
  link?: string;
  agentTasks?: string[];
};

interface Props {
  stages: Stage[];
  overallProgress: number;
  titleKey?: string;
  infoTextKey?: string;
}

export function CockpitSalesStageWidget({
  stages = [],
  overallProgress = 0,
  titleKey = 'cockpit_sales_stage_title',
  infoTextKey = 'cockpit_sales_stage_info',
}: Props) {
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAgent = atLeast('partner');
  //// const params = useAppParams();

  const getStageColor = (status: StageStatus) => {
    switch (status) {
      case 'complete':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'upcoming':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 dark:text-gray-200 border-gray-200 dark:border-gray-700';
      case 'blocked':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 dark:text-gray-200 border-gray-200 dark:border-gray-700';
    }
  };

  const replaceParamsInLink = (link?: string) => link || undefined;

  return (
    <WrapperWidget
      title={t(titleKey)}
      text={t(infoTextKey)}
    >
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>{t('cockpit_sales_overall_progress')}</span>
            <span>{Math.round(overallProgress)}%</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
        <div className="space-y-2">
          {(stages || []).map((stage, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3"
            >
              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
              <div
                className={`flex-1 p-2 rounded-md border ${getStageColor(
                  stage.status,
                )}`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm">
                    {t(`cockpit_sales_stage_${stage.key}`) || stage.labelKey}
                  </span>
                  {stage.kpi !== undefined && (
                    <span className="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-slate-200 dark:ring-slate-700 dark:ring-gray-600 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 dark:text-gray-100 shadow-sm dark:shadow-gray-900/50">
                      {stage.kpi}
                    </span>
                  )}
                </div>
                {isAgent && stage.agentTasks?.length > 0 && (
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                    {stage.agentTasks.map((task, tIdx) => (
                      <span
                        key={tIdx}
                        className="block"
                      >
                        • {task}
                      </span>
                    ))}
                  </div>
                )}
                {stage.link && (
                  <a
                    href={replaceParamsInLink(stage.link)}
                    className="block mt-1 text-xs underline text-blue-600 hover:text-blue-700"
                  >
                    {t('cockpit_sales_stage_details')}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </WrapperWidget>
  );
}
